import { Options, Select } from '@/components/Controls/Select';
import { NotFound } from '@/components/Message/NotFound';
import { TFunction } from 'i18next';

type UserFilterProps = {
  t: TFunction<undefined>;
  isLoadingAllGroups?: boolean;
  isLoadingAllCompanies?: boolean;
  isLoadingAllBranches?: boolean;
  groupOptions?: Options[];
  companyOptions?: Options[];
  branchOptions?: Options[];
  displayGroupFilter?: boolean;
  displayCompanyFilter?: boolean;
  displayBranchFilter?: boolean;
  groupId?: string;
  companyId?: string;
  branchId?: string;
  searchByGroup?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByCompany?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByBranch?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

const UserFilter = ({
  t,
  isLoadingAllGroups,
  isLoadingAllCompanies,
  isLoadingAllBranches,
  groupOptions,
  companyOptions,
  branchOptions,
  displayGroupFilter,
  displayBranchFilter,
  displayCompanyFilter,
  groupId,
  companyId,
  branchId,
  searchByGroup,
  searchByCompany,
  searchByBranch,
}: UserFilterProps) => {
  return (
    <div className="flex flex-col gap-3">
      {displayGroupFilter && (
        <div className="flex items-center gap-16">
          <div className="flex flex-1 text-gray-600 text-sm">
            {t('common:linkedToGroup')}
          </div>
          <div className="min-w-60 flex flex-1">
            {isLoadingAllGroups ? (
              <Select
                selectClassName="h-[36px] min-w-60 bg-gray-FCFCFC dark-mode:bg-coal-500"
                placeholder={t('common:allGroups')}
                options={[]}
              />
            ) : (
              <Select
                selectClassName="h-[36px] min-w-60 bg-gray-FCFCFC dark-mode:bg-coal-500"
                placeholder={t('common:allGroups')}
                options={groupOptions}
                value={groupId || ''}
                onChange={searchByGroup}
              />
            )}
          </div>
        </div>
      )}
      {displayCompanyFilter && (
        <div className="flex items-center gap-16">
          <div className="flex flex-1 text-gray-600 text-sm">
            {t('common:linkedToCompany')}
          </div>
          <div className="min-w-60 flex flex-1">
            {isLoadingAllCompanies ? (
              <Select
                selectClassName="h-[36px] min-w-60 bg-gray-FCFCFC dark-mode:bg-coal-500"
                placeholder={t('common:allCompanies')}
                options={[]}
              />
            ) : groupId && companyOptions.length === 0 ? (
              <NotFound
                containerClassName="max-w-60 gap-1.5"
                iconClassName="-mt-1.5 text-lg"
                textClassName="text-2sm"
                text={t('common:noCompanyLinkedToThisGroup')}
              />
            ) : companyOptions.length === 0 ? (
              <NotFound
                containerClassName="max-w-60 gap-1.5"
                iconClassName="-mt-1.5 text-lg"
                textClassName="text-2sm"
                text={t('common:youAreNotLinkedToAnyCompany')}
              />
            ) : (
              <Select
                selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                placeholder={t('common:allCompanies')}
                options={companyOptions}
                value={companyId || ''}
                onChange={searchByCompany}
              />
            )}
          </div>
        </div>
      )}
      {displayBranchFilter && (
        <div className="flex items-center gap-16">
          <div className="flex flex-1 text-gray-600 text-sm">
            {t('common:linkedToBranch')}
          </div>
          <div className="min-w-60 flex flex-1">
            {isLoadingAllBranches ? (
              <Select
                selectClassName="h-[36px] min-w-60 bg-gray-FCFCFC dark-mode:bg-coal-500"
                placeholder={t('common:allBranches')}
                options={[]}
              />
            ) : companyId && branchOptions.length === 0 ? (
              <NotFound
                containerClassName="max-w-60 gap-1.5"
                iconClassName="-mt-1.5 text-lg"
                textClassName="text-2sm"
                text={t('common:noBranchLinkedToThisCompany')}
              />
            ) : groupId && branchOptions.length === 0 ? (
              <NotFound
                containerClassName="max-w-60 gap-1.5"
                iconClassName="-mt-1.5 text-lg"
                textClassName="text-2sm"
                text={t('common:noBranchLinkedToThisGroup')}
              />
            ) : branchOptions.length === 0 ? (
              <NotFound
                containerClassName="gap-1.5"
                iconClassName="-mt-1.5 text-lg"
                textClassName="text-2sm"
                text={t('common:youAreNotLinkedToAnyBranch')}
              />
            ) : (
              <Select
                selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                placeholder={t('common:allBranches')}
                options={branchOptions}
                value={branchId || ''}
                onChange={searchByBranch}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFilter;
